import { z } from 'zod';

// ─── Recurrence `scheduleJson` (v1) & `requestJson.dateRangePreset` ─────────

/** ISO weekday: Monday = 1 … Sunday = 7 (ISO 8601). */
export const isoWeekdaySchema = z.number().int().min(1).max(7);

export const scheduleRuleDailySchema = z.object({
  kind: z.literal('daily'),
  hour: z.number().int().min(0).max(23),
  minute: z.number().int().min(0).max(59),
  intervalDays: z.number().int().positive().default(1),
});

export const scheduleRuleWeeklySchema = z.object({
  kind: z.literal('weekly'),
  weekday: isoWeekdaySchema,
  hour: z.number().int().min(0).max(23),
  minute: z.number().int().min(0).max(59),
  intervalWeeks: z.number().int().positive().default(1),
});

export const scheduleRuleMonthlySchema = z.object({
  kind: z.literal('monthly'),
  dayOfMonth: z.number().int().min(1).max(31),
  hour: z.number().int().min(0).max(23),
  minute: z.number().int().min(0).max(59),
  intervalMonths: z.number().int().positive().default(1),
});

export const scheduleRuleRecurrenceV1Schema = z.discriminatedUnion('kind', [
  scheduleRuleDailySchema,
  scheduleRuleWeeklySchema,
  scheduleRuleMonthlySchema,
]);

export const DATE_RANGE_PRESET_VALUES = [
  'previous_day',
  'previous_full_iso_week',
  'previous_full_month',
  'last_7_complete_days',
  'last_14_complete_days',
  'last_30_complete_days',
  'last_90_complete_days',
  'last_4_complete_iso_weeks',
  'last_8_complete_iso_weeks',
  'last_12_complete_iso_weeks',
  'last_3_complete_months',
  'last_6_complete_months',
  'last_12_complete_months',
  'week_to_previous_full_day',
  'month_to_previous_full_day',
  'year_to_previous_full_day',
] as const;

export const dateRangePresetSchema = z.enum(DATE_RANGE_PRESET_VALUES);

export type DateRangePreset = z.infer<typeof dateRangePresetSchema>;

export const DATE_RANGE_PRESET_LABELS: Record<DateRangePreset, string> = {
  previous_day: 'Previous calendar day',
  previous_full_iso_week: 'Previous full ISO week (Mon–Sun)',
  previous_full_month: 'Previous full calendar month',
  last_7_complete_days: 'Last 7 complete calendar days',
  last_14_complete_days: 'Last 14 complete calendar days',
  last_30_complete_days: 'Last 30 complete calendar days',
  last_90_complete_days: 'Last 90 complete calendar days',
  last_4_complete_iso_weeks: 'Last 4 complete ISO weeks',
  last_8_complete_iso_weeks: 'Last 8 complete ISO weeks',
  last_12_complete_iso_weeks: 'Last 12 complete ISO weeks',
  last_3_complete_months: 'Last 3 complete calendar months',
  last_6_complete_months: 'Last 6 complete calendar months',
  last_12_complete_months: 'Last 12 complete calendar months',
  week_to_previous_full_day: 'This week through yesterday',
  month_to_previous_full_day: 'This month through yesterday',
  year_to_previous_full_day: 'This year through yesterday',
};

/** One-line explanation under the preset control (account / export timezone is applied by the API). */
export const DATE_RANGE_PRESET_HELPER_TEXTS: Record<DateRangePreset, string> = {
  previous_day:
    'The single calendar day before the run. Early runs can still see revisions to that day’s figures.',
  previous_full_iso_week:
    'The ISO week (Monday–Sunday) immediately before the week that contains the run. The current week is excluded.',
  previous_full_month:
    'The entire calendar month before the month that contains the run. The current month is excluded.',
  last_7_complete_days:
    'The seven most recent finished calendar days, ending the day before the run (today is never included).',
  last_14_complete_days:
    'The fourteen most recent finished calendar days, ending the day before the run.',
  last_30_complete_days:
    'The thirty most recent finished calendar days, ending the day before the run.',
  last_90_complete_days:
    'The ninety most recent finished calendar days, ending the day before the run.',
  last_4_complete_iso_weeks:
    'The four most recent full Monday–Sunday weeks, excluding any partial current week.',
  last_8_complete_iso_weeks:
    'The eight most recent full Monday–Sunday weeks, excluding any partial current week.',
  last_12_complete_iso_weeks:
    'The twelve most recent full Monday–Sunday weeks, excluding any partial current week.',
  last_3_complete_months:
    'The three most recent full calendar months, excluding the current partial month.',
  last_6_complete_months:
    'The six most recent full calendar months, excluding the current partial month.',
  last_12_complete_months:
    'The twelve most recent full calendar months, excluding the current partial month.',
  week_to_previous_full_day:
    'From the start of the calendar week (Monday) through the end of the day before the run.',
  month_to_previous_full_day:
    'From the first day of the calendar month through the end of the day before the run.',
  year_to_previous_full_day:
    'From 1 January of the calendar year through the end of the day before the run.',
};

/** Grouped order for the schedule form dropdown (every preset appears once). */
export const DATE_RANGE_PRESET_UI_GROUPS: {
  heading: string;
  values: readonly DateRangePreset[];
}[] = [
  {
    heading: 'Single calendar periods',
    values: ['previous_day', 'previous_full_iso_week', 'previous_full_month'],
  },
  {
    heading: 'Rolling complete days',
    values: [
      'last_7_complete_days',
      'last_14_complete_days',
      'last_30_complete_days',
      'last_90_complete_days',
    ],
  },
  {
    heading: 'Rolling complete ISO weeks',
    values: [
      'last_4_complete_iso_weeks',
      'last_8_complete_iso_weeks',
      'last_12_complete_iso_weeks',
    ],
  },
  {
    heading: 'Rolling complete calendar months',
    values: ['last_3_complete_months', 'last_6_complete_months', 'last_12_complete_months'],
  },
  {
    heading: 'Current period through yesterday',
    values: [
      'week_to_previous_full_day',
      'month_to_previous_full_day',
      'year_to_previous_full_day',
    ],
  },
];

const positiveIntIdArraySchema = z.array(z.number().int().positive());

export const statScheduleRequestJsonV1Schema = z.object({
  schemaVersion: z.literal(1),
  dateRangePreset: dateRangePresetSchema,
  clientIds: positiveIntIdArraySchema.optional(),
  productIds: positiveIntIdArraySchema.optional(),
  /** Contextual rules (`entity_attribute`, etc.) — preserved; typed when rule UI ships. */
  filters: z.array(z.unknown()).optional(),
});

export type StatScheduleRequestJsonV1 = z.infer<typeof statScheduleRequestJsonV1Schema>;

export type BuildStatScheduleRequestJsonV1Args = {
  dateRangePreset: DateRangePreset;
  clientIds?: number[];
  productIds?: number[];
  /** Pass through on update when the form does not edit contextual rules yet. */
  filters?: unknown[];
};

/** Builds `requestJson` for POST/PUT/run-once; omits empty scope arrays and empty `filters`. */
export function buildStatScheduleRequestJsonV1(
  args: BuildStatScheduleRequestJsonV1Args,
): StatScheduleRequestJsonV1 {
  const body: Record<string, unknown> = {
    schemaVersion: 1,
    dateRangePreset: args.dateRangePreset,
  };
  if (args.clientIds?.length) {
    body.clientIds = args.clientIds;
  }
  if (args.productIds?.length) {
    body.productIds = args.productIds;
  }
  if (args.filters?.length) {
    body.filters = args.filters;
  }
  return statScheduleRequestJsonV1Schema.parse(body);
}

export type ScheduleRuleRecurrenceV1 = z.infer<typeof scheduleRuleRecurrenceV1Schema>;

/** UI options: ISO weekday 1 = Monday … 7 = Sunday. */
export const ISO_WEEKDAY_OPTIONS: { value: z.infer<typeof isoWeekdaySchema>; label: string }[] = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 7, label: 'Sunday' },
];

export type StatScheduleRecurrenceFormSlice = {
  scheduleKind: 'daily' | 'weekly' | 'monthly';
  hour: number;
  minute: number;
  weekday: number;
  dayOfMonth: number;
};

function toRawScheduleRuleRecurrenceV1(args: StatScheduleRecurrenceFormSlice) {
  if (args.scheduleKind === 'daily') {
    return {
      kind: 'daily',
      hour: args.hour,
      minute: args.minute,
      /** Only `1` is supported by the API for now. */
      intervalDays: 1,
    };
  }
  if (args.scheduleKind === 'weekly') {
    return {
      kind: 'weekly',
      weekday: args.weekday,
      hour: args.hour,
      minute: args.minute,
      /** UI does not expose interval; always every week. */
      intervalWeeks: 1,
    };
  }
  return {
    kind: 'monthly',
    dayOfMonth: args.dayOfMonth,
    hour: args.hour,
    minute: args.minute,
    /** UI does not expose interval; always every month. */
    intervalMonths: 1,
  };
}

/** Validates and returns `scheduleJson` for POST/PUT. */
export function buildStatScheduleRuleRecurrenceV1(
  args: StatScheduleRecurrenceFormSlice,
): ScheduleRuleRecurrenceV1 {
  return scheduleRuleRecurrenceV1Schema.parse(toRawScheduleRuleRecurrenceV1(args));
}

export function safeParseStatScheduleRuleRecurrenceV1(args: StatScheduleRecurrenceFormSlice) {
  return scheduleRuleRecurrenceV1Schema.safeParse(toRawScheduleRuleRecurrenceV1(args));
}

/**
 * Mirrors `GET /authenticated/{accountType}/{accountId}/analytics/schedules` list items.
 * @see https://juxh0nlz7j.execute-api.us-east-1.amazonaws.com/docs — Analytics → Stat schedules
 */
export const statScheduleTargetSchema = z.object({
  publicId: z.string(),
  channel: z.string(),
  targetKind: z.enum(['user', 'email']),
  userId: z.number().nullable(),
  email: z.string().nullable(),
  displayName: z.string().nullable(),
  disabledAt: z.string().nullable(),
  disabledReason: z.string().nullable(),
});

export const statScheduleListItemSchema = z.object({
  publicId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  statKey: z.string(),
  viewPublicId: z.string(),
  timezone: z.string(),
  scheduleKind: z.enum(['daily', 'weekly', 'monthly']),
  scheduleJson: z.unknown(),
  requestJson: z.unknown(),
  reportFormat: z.enum(['json', 'csv', 'xlsx']),
  attachFile: z.boolean(),
  nextRunAt: z.string().nullable().optional(),
  lastScheduledAt: z.string().nullable().optional(),
  disabledAt: z.string().nullable().optional(),
  createdAt: z.string(),
  createdBy: z.string().nullable(),
  updatedAt: z.string(),
  targets: z.array(statScheduleTargetSchema),
});

export const statSchedulesListResponseSchema = z.array(statScheduleListItemSchema);

/** Request body for `POST` / `PUT …/analytics/schedules` (no server-generated fields). */
export const statScheduleTargetWriteSchema = z.object({
  /** Include on `PUT` when updating existing targets. */
  publicId: z.string().optional(),
  channel: z.string(),
  targetKind: z.enum(['user', 'email']),
  userId: z.number().nullable().optional(),
  email: z.string().nullable().optional(),
  displayName: z.string().nullable().optional(),
});

export const statScheduleUpsertBodySchema = z.object({
  name: z.string().min(1),
  description: z.string().nullable(),
  statKey: z.string(),
  viewPublicId: z.string(),
  timezone: z.string(),
  scheduleKind: z.enum(['daily', 'weekly', 'monthly']),
  scheduleJson: z.unknown(),
  requestJson: z.unknown(),
  reportFormat: z.enum(['json', 'csv', 'xlsx']),
  attachFile: z.boolean(),
  targets: z.array(statScheduleTargetWriteSchema).min(1),
});

export type StatScheduleListItem = z.infer<typeof statScheduleListItemSchema>;
export type StatScheduleTarget = z.infer<typeof statScheduleTargetSchema>;
export type StatScheduleUpsertBody = z.infer<typeof statScheduleUpsertBodySchema>;

/** Request body for `POST …/analytics/schedules/run-once` (no schedule row; delivery is current user only). */
export const statScheduleRunOnceBodySchema = z.object({
  statKey: z.string().min(1).max(100),
  viewPublicId: z.string().min(1).max(64),
  requestJson: statScheduleRequestJsonV1Schema,
  reportFormat: z.enum(['json', 'csv', 'xlsx']),
  attachFile: z.boolean(),
});

export type StatScheduleRunOnceBody = z.infer<typeof statScheduleRunOnceBodySchema>;

/** `202` response from `run-once` when the job is queued. */
export const statScheduleRunOnceQueuedResponseSchema = z.object({
  async: z.literal(true),
  status: z.literal('queued'),
  jobPublicId: z.string(),
});

export type StatScheduleRunOnceQueuedResponse = z.infer<
  typeof statScheduleRunOnceQueuedResponseSchema
>;

/** Build a `PUT`/`POST`-compatible body from a list row (includes target `publicId`s for updates). */
export function statScheduleListItemToUpsertBody(
  row: StatScheduleListItem,
): StatScheduleUpsertBody {
  return statScheduleUpsertBodySchema.parse({
    name: row.name,
    description: row.description,
    statKey: row.statKey,
    viewPublicId: row.viewPublicId,
    timezone: row.timezone,
    scheduleKind: row.scheduleKind,
    scheduleJson: row.scheduleJson,
    requestJson: row.requestJson,
    reportFormat: row.reportFormat,
    attachFile: row.attachFile,
    targets: row.targets.map((t) => ({
      publicId: t.publicId,
      channel: t.channel,
      targetKind: t.targetKind,
      userId: t.userId,
      email: t.email,
      displayName: t.displayName,
    })),
  });
}
